import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { ApiError } from '../lib/api';
import { BrandMark } from '../components/BrandMark';
import { PasswordField } from '../components/ui/PasswordField';

/**
 * Sign-in for frontline staff.
 *
 * Deliberately plain: one field above the other, large targets, and the submit button
 * last so it sits under a thumb. Staff sign in at the start of a shift, often
 * one-handed while walking.
 */
/**
 * How long to wait, in a unit somebody can act on.
 *
 * Rounded up, never down: telling a technician to wait a minute when the server will
 * still refuse them for ninety seconds just buys a second failed attempt.
 */
function throttledMessage(t: TFunction, seconds?: number): string {
  if (seconds === undefined) return t('login.throttledUnknown');
  if (seconds < 60) return t('login.throttledSeconds', { count: seconds });
  return t('login.throttledMinutes', { count: Math.ceil(seconds / 60) });
}

export function LoginPage() {
  const { t } = useTranslation();
  const { signIn } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
      // No navigation here: the router swaps the tree as soon as user is set.
    } catch (caught) {
      // A throttled attempt is the one failure worth distinguishing. Telling somebody
      // their password was not recognised when the server never looked at it would send
      // them round the same loop, adding failures to a cooldown they cannot see.
      setError(
        caught instanceof ApiError && caught.status === 429
          ? throttledMessage(t, caught.retryAfterSeconds)
          : // Otherwise: one message for every failure, because the API deliberately
            // returns one for every failure so accounts cannot be enumerated.
            t('login.failed'),
      );
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-dvh flex-col justify-center bg-slate-950 px-6 py-10 text-slate-100">
      <div className="mx-auto w-full max-w-sm">
        {/*
          The brand hero (D28). Green ground, gold rule, the name centred on it.
          Set as a wordmark rather than an image because no logo file was supplied —
          swapping in an <img> later changes this block and nothing else.

          Gold-light is used for the strapline rather than gold proper: 5.50:1 on this
          green against gold's 3.31:1, and a strapline is text, not an accent.

          The green itself is a top-lit gradient rather than a flat fill, and the
          rule beneath it a gradient too (the same `border-image` motif as the app
          header) — the first thing every shift starts with is worth the extra
          attention the frontend-design brief asks for, and "flat rectangle, flat
          line" was leaving that hero doing no more than naming the app.
        */}
        <div className="flex flex-col items-center rounded-2xl border-b-4 border-transparent bg-gradient-to-b from-brookfields-green-light via-brookfields-green to-brookfields-green-dark px-6 py-8 text-center shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12)] [border-image:linear-gradient(to_right,var(--color-brookfields-gold-dark),var(--color-brookfields-gold-light),var(--color-brookfields-gold-dark))_1]">
          <BrandMark size="size-16" />
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-white">{t('app.name')}</h1>
          <p className="mt-1 text-sm text-brookfields-gold-light">{t('app.tagline')}</p>
        </div>

        <p className="mt-6 text-sm text-slate-400">{t('login.subtitle')}</p>

        <form className="mt-8 flex flex-col gap-4" onSubmit={handleSubmit}>
          <label className="block">
            <span className="mb-1 block text-sm text-slate-300">{t('login.email')}</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-3 text-base"
            />
          </label>

          <PasswordField
            label={t('login.password')}
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            required
          />

          {error && (
            <p role="alert" className="text-sm text-red-400">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy || !email.trim() || !password}
            // A top-lit gradient and an inset highlight rather than a flat fill —
            // the same "catches light" treatment as the hero above it, so the one
            // button on this whole screen reads as a control rather than a shape.
            className="mt-2 flex min-h-[56px] w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-brookfields-green-light to-brookfields-green shadow-[inset_0_1px_0_0_rgba(255,255,255,0.16)] ring-1 ring-brookfields-gold/70 text-base font-semibold text-white disabled:bg-brookfields-green-dark disabled:bg-none disabled:text-slate-400 disabled:shadow-none disabled:ring-brookfields-gold-dark"
          >
            {busy && <Loader2 className="size-5 animate-spin" aria-hidden="true" />}
            {busy ? t('login.signingIn') : t('login.signIn')}
          </button>
        </form>
      </div>
    </main>
  );
}
